/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

import React, { Component } from 'react';
import Carousel from '@ccxi/ccxi-shared-components/src/Carousel';
import './index.scss';
import AppConfig from '../../stores/config';

export default class CardGallery extends Component {
    componentDidMount() {
        const htmlEle = document.getElementsByTagName('html')[0];
        const isEdgeBrowser = htmlEle && htmlEle.getAttribute('data-browser') === 'edge';
        const videos = this.gallery.querySelectorAll('.appCard-media-gallery-video video');
        const videoArr = Array.prototype.slice.call(videos, 0);
        if (isEdgeBrowser && videoArr.length) {
            // scales videos to remove white space
            // Bug fix CCH-10135
            videoArr.forEach((video) => {
                video.addEventListener('loadeddata', () => {
                    this.scaleVideo(video);
                }, { once: true });
            });

            window.addEventListener('resize', this.handleVideosOnWindowResize);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleVideosOnWindowResize);
    }

    onCarouselClick = ({ triggerType }) => {
        const { app } = this.props;
        const { sapcode, appFamilyName, codexVersion } = app;
        this.sendAnalytics({
            event: {
                workflow: 'Debug',
                subcategory: 'CCD Apps',
                subtype: 'Log',
                type: 'info',
                value: 'carousel',
            },
            ccd: {
                event_subcategory: 'Category Pages',
                event_type: 'click',
                event_subtype: triggerType,
            },
            content: {
                id: sapcode,
                name: appFamilyName,
                version: codexVersion,
            },
        });
    }

    videoHoverAnalytics = () => {
        const { app, mediaList } = this.props;
        const { sapcode, appFamilyName, codexVersion } = app;
        let name = '';
        if (mediaList && mediaList.length >= 1 && mediaList[0].video) {
            [{ video: { name } }] = mediaList;
        }
        this.sendAnalytics({
            event: {
                workflow: 'Debug',
                subcategory: 'CCD Apps',
                subtype: 'Log',
                type: 'info',
                value: 'video',
            },
            ccd: {
                event_subcategory: 'Category Pages',
                event_type: 'hover',
                event_value: name,
            },
            content: {
                id: sapcode,
                name: appFamilyName,
                version: codexVersion,
            },
        });
    }

    renderMedia = () => {
        const {
            mediaList,
            onMediaClick,
            mediaClassName,
            isMobileOrTab,
        } = this.props;
        return mediaList.map((media) => {
            if (media.video) {
                const url = media.video.thumbnailImage
                    && media.video.thumbnailImage.file
                    && media.video.thumbnailImage.file.url;
                const publishUrl = media.video.thumbnailImage
                    // eslint-disable-next-line no-underscore-dangle
                    && media.video.thumbnailImage._publishUrl;
                /* eslint-disable no-nested-ternary */
                return (
                    <div
                        className={`appCard-media-gallery-video ${mediaClassName}`}
                        onClick={onMediaClick}
                        onMouseEnter={this.videoHoverAnalytics}>
                        <video
                            loop
                            playsInline
                            muted
                            controls={isMobileOrTab}
                            poster={url || publishUrl}>
                            {media.video.mp4 && <source src={media.video.mp4} type="video/mp4" />}
                            {media.video.ogg && <source src={media.video.ogg} type="video/ogg" />}
                        </video>
                    </div>
                );
                /* eslint-enable no-nested-ternary */
            }
            if (media.image) {
                const {
                    file: {
                        url,
                    } = {},
                    _publishUrl,
                } = media.image;
                return (
                    <div
                        className={`appCard-media-gallery-image ${mediaClassName}`}
                        onClick={onMediaClick}
                        style={{ backgroundImage: `url('${url || _publishUrl}')` }} />
                );
            }
            return null;
        });
    }

    renderMediaCarousel = () => {
        const carouselCardMap = {
            L: {
                breakpointL: {
                    gutter: 8,
                    cardsPerRow: 1,
                },
                breakpointLM: {
                    gutter: 8,
                    cardsPerRow: 1,
                },
                breakpointM: {
                    gutter: 8,
                    cardsPerRow: 1,
                },
                breakpointMS: {
                    gutter: 8,
                    cardsPerRow: 1,
                },
                breakpointS: {
                    gutter: 8,
                    cardsPerRow: 1,
                },
                breakpointSS: {
                    gutter: 8,
                    cardsPerRow: 1,
                },
                breakpointXS: {
                    gutter: 8,
                    cardsPerRow: 1,
                },
            },
        };
        return (
            <Carousel
                cardSize="L"
                customCardMapping={carouselCardMap}
                showPagerDot
                onCarouselClick={this.onCarouselClick}
                skipAccessibility>
                {this.renderMedia()}
            </Carousel>
        );
    }

    scaleVideo = (vid) => {
        const actualRatio = vid.videoWidth / vid.videoHeight;
        const targetRatio = this.gallery.offsetWidth / this.gallery.offsetHeight;
        let scaleX = 1;
        let scaleY = 1;
        if (actualRatio < targetRatio) {
            scaleX = targetRatio / actualRatio;
        } else {
            scaleY = actualRatio / targetRatio;
        }
        vid.style.webkitTransform = `scale(${scaleX},${scaleY})`; // eslint-disable-line no-param-reassign
    }

    handleVideosOnWindowResize = () => {
        if (this.throttled) {
            return;
        }
        this.throttled = true;

        const videos = this.gallery.querySelectorAll('.appCard-media-gallery-video video');
        const videoArr = Array.prototype.slice.call(videos, 0);
        videoArr.forEach(this.scaleVideo);

        setTimeout(() => {
            this.throttled = false;
        }, 200);
    }

    sendAnalytics(analyticObject) {
        AppConfig.analyticsCallback({
            componentParams: {},
            analyticObjectList: [analyticObject],
        });
    }

    render() {
        return (
            <div
                className="appCard-media-gallery"
                ref={(element) => { this.gallery = element; }}>
                {this.renderMediaCarousel()}
            </div>
        );
    }
}
